import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, AbstractControl, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { Subject, takeUntil } from 'rxjs';
import { PriceFileSimulationService } from '../../shared-services/price-file-simulation.service';
// import { formatDisplayText } from '../../utils/text-case.utils';

@Component({
  selector: 'app-filter-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonToggleModule
  ],
  templateUrl: './filter-dialog.component.html',
  styleUrls: ['./filter-dialog.component.scss']
})
export class FilterDialogComponent implements OnInit, OnDestroy {
  queryForm!: FormGroup;
  private unSubscribe$ = new Subject<void>();
  private readonly simValueDropFields = new Set([
    'ecc',
    'regional_pricing_flag',
    'saleable',
    'pricing_admin_owner',
    'product_bu',
    'segment_type',
    'region',
    'pricing_action',
    'change_reason',
    'brand',
    'part_owner',
    'engine_model',
    'swapp',
    'information'
  ]);
  private valueDropdownOptions = new WeakMap<AbstractControl, string[]>();
  private fieldSearchTextByRule = new WeakMap<AbstractControl, string>();
  private operatorSearchTextByRule = new WeakMap<AbstractControl, string>();
  private valueSearchTextByRule = new WeakMap<AbstractControl, string>();

  fields: any[] = [];
  operators: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<FilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private priceFileSimulationService: PriceFileSimulationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (Array.isArray(this.data?.fieldOptions) && this.data.fieldOptions.length > 0) {
      const fieldMap = new Map<string, string>();
      this.data.fieldOptions.forEach((field: any) => {
        const raw = String(field ?? '').trim();
        if (raw) {
          const token = this.normalizeFieldToken(raw);
          if (!fieldMap.has(token)) {
            fieldMap.set(token, raw);
          }
        }
      });

      this.fields = Array.from(fieldMap.entries()).map(([value, viewValue]) => ({
        value,
        viewValue
      }));
    }

    if (Array.isArray(this.data?.operatorOptions) && this.data.operatorOptions.length > 0) {
      const mappedOperators = this.data.operatorOptions
        .map((operator: any) => String(operator ?? '').trim())
        .filter((operator: string) => operator !== '')
        .map((operator: string) => ({
          value: operator,
          viewValue: this.toOperatorDisplayText(operator)
        }));

      const hasIsNull = mappedOperators.some((op: any) =>
        ['is null', 'is_null'].includes(String(op?.value || '').trim().toLowerCase())
      );
      const hasIsNotNull = mappedOperators.some((op: any) =>
        ['is not null', 'is_not_null'].includes(String(op?.value || '').trim().toLowerCase())
      );

      this.operators = [
        ...mappedOperators,
        ...(hasIsNull ? [] : [{ value: 'is_null', viewValue: 'Is Null' }]),
        ...(hasIsNotNull ? [] : [{ value: 'is_not_null', viewValue: 'Is not Null' }])
      ];
    }

    if (Array.isArray(this.data?.tableColumns) && this.data.tableColumns.length > 0) {
      if (Array.isArray(this.data?.fieldOptions) && this.data.fieldOptions.length > 0) {
        this.queryForm = this.createRuleSetGroup();
        this.prefillFromInitialFilters();
        return;
      }
      // UI Standards (Point 3): enforce Capital Case on field names for consistency
      this.fields = this.data.tableColumns
        .filter((col: any) => col && col.key)
        .map((col: any) => ({
          value: col.key,
          viewValue: col.label || col.key
        }));
    }
    this.queryForm = this.createRuleSetGroup();
    this.prefillFromInitialFilters();
  }

  private toOperatorDisplayText(operator: string): string {
    const op = String(operator || '').trim().toLowerCase();
    const operatorLabelMap: Record<string, string> = {
      '=': 'Is equal to',
      '!=': 'Is not equal to',
      '<': 'Less than',
      '<=': 'Less than or equal to',
      '>': 'Greater than',
      '>=': 'Greater than or equal to',
      'in': 'In',
      'not in': 'Not In',
      'not_in': 'Not In',
      'is null': 'Is Null',
      'is_null': 'Is Null',
      'is not null': 'Is not Null',
      'is_not_null': 'Is not Null',
      'contains': 'Contains'
    };
    return operatorLabelMap[op] || operator;
  }

  createRuleSetGroup(): FormGroup {
    return this.fb.group({
      condition: ['AND'],
      rules: this.fb.array([])
    });
  }

  createRuleGroup(): FormGroup {
    const group = this.fb.group({
      field: ['', Validators.required],
      operator: ['', Validators.required],
      value: ['', Validators.required]
    });

    this.updateValueValidatorForOperator(group);
    group.get('field')?.valueChanges.subscribe((field) => this.onFieldChange(group, field));
    group.get('operator')?.valueChanges.subscribe(() => this.updateValueValidatorForOperator(group));
    return group;
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  getRules(formGroup: AbstractControl): FormArray {
    return formGroup.get('rules') as FormArray;
  }

  addRule(formGroup: AbstractControl): void {
    this.getRules(formGroup).push(this.createRuleGroup());
  }

  addRuleSet(formGroup: AbstractControl): void {
    const existingRules = this.getRules(formGroup);
    const nestedRuleSet = this.createRuleSetGroup();
    const nestedRules = this.getRules(nestedRuleSet);

    for (let i = 0; i < 2; i++) {
      nestedRules.push(this.createRuleGroup());
    }

    existingRules.push(nestedRuleSet);
  }

  removeRule(formGroup: AbstractControl, index: number): void {
    this.getRules(formGroup).removeAt(index);
  }

  isRuleSet(control: AbstractControl): boolean {
    return !!control.get('rules');
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onApply(): void {
    // Allow applying with no rules (empty filter) or with valid rules
    const hasRules = this.hasAnyRules(this.queryForm);
    const isValidWithRules = !hasRules || this.queryForm.valid;

    if (isValidWithRules) {
      const formattedQuery = this.formatQuery(this.queryForm);
      console.log('Formatted Query:', formattedQuery);
      this.dialogRef.close({
        ...formattedQuery,
        formState: this.queryForm.getRawValue()
      });
    } else {
      this.queryForm.markAllAsTouched();
    }
  }

  isApplyDisabled(): boolean {
    // Disable Apply only if there are rules but they're invalid
    const hasRules = this.hasAnyRules(this.queryForm);
    if (!hasRules) {
      return false; // Allow applying with no rules (empty filter)
    }
    return this.queryForm.invalid;
  }

  hasAnyRules(group: AbstractControl): boolean {
    const rules = this.getRules(group).controls;
    if (!rules.length) {
      return false;
    }

    return rules.some((control) => {
      if (!this.isRuleSet(control)) {
        return true;
      }
      return this.hasAnyRules(control);
    });
  }

  hasAnyValidRules(group: AbstractControl): boolean {
    const rules = this.getRules(group).controls;
    if (!rules.length) {
      return false;
    }

    return rules.some((control) => {
      if (!this.isRuleSet(control)) {
        return control.valid;
      }
      return this.hasAnyValidRules(control);
    });
  }

  private formatQuery(group: AbstractControl): { filters: any[]; appliedFilters: any[] } {
    const formValue = group.getRawValue();
    return {
      filters: this.transformRules(formValue.rules, formValue.condition),
      appliedFilters: this.toAppliedFilters(group as FormGroup)
    };
  }

  private transformRules(rules: any[], parentCondition: string): any[] {
    if (!rules) {
      return [];
    }
    return rules.map(rule => {
      if (rule.rules) {
        // This is a rule set (group)
        return {
          filterType: 'CONDITIONGROUP',
          joinType: parentCondition,
          value: '',
          filters: this.transformRules(rule.rules, rule.condition),
           dataType: 'string'
        };
      } else {
        // This is a single rule (condition)
        const operator = this.mapOperator(rule.operator);
        return {
          filterType: 'CONDITION',
          joinType: parentCondition,
          operatorType: operator,
          key: rule.field,
          value: (operator === 'IS_NULL' || operator === 'IS_NOT_NULL') ? 'null' : rule.value,
          dataType: 'string' // Assuming string for now
        };
      }
    });
  }

  private mapOperator(op: string): string {
    const mapping: { [key: string]: string } = {
      'is equal to': 'EQUALS',
      'is not equal to': 'IS_NOT_EQUAL',
      'in': 'IN',
      'not in': 'NOT_IN',
      'contains': 'CONTAINS',
      'greater than': 'GREATER_THAN',
      'less than': 'LESS_THAN',
      'greater than or equal to': 'GREATER_THAN_OR_EQUAL',
      'less than or equal to': 'LESS_THAN_OR_EQUAL',
      'equal': 'EQUALS',
      'not_equal': 'IS_NOT_EQUAL',
      'not_in': 'NOT_IN',
      'greater_than': 'GREATER_THAN',
      'less_than': 'LESS_THAN',
      'greater_than_or_equal': 'GREATER_THAN_OR_EQUAL',
      'less_than_or_equal': 'LESS_THAN_OR_EQUAL',
      'is null': 'IS_NULL',
      'is_null': 'IS_NULL',
      'is not null': 'IS_NOT_NULL',
      'is_not_null': 'IS_NOT_NULL'
    };
    const normalized = String(op || '').trim().toLowerCase();
    return mapping[normalized] || String(op || '').toUpperCase();
  }

  private updateValueValidatorForOperator(ruleGroup: FormGroup): void {
    const operatorControl = ruleGroup.get('operator');
    const valueControl = ruleGroup.get('value');
    const operator = String(operatorControl?.value || '').trim().toLowerCase();
    const normalizedOperator = operator.replace(/\s+/g, '_');
    const isNullCheck =
      normalizedOperator === 'is_null'
      || normalizedOperator === 'is_not_null';

    if (isNullCheck) {
      valueControl?.setValue('', { emitEvent: false });
      valueControl?.clearValidators();
    } else {
      valueControl?.setValidators([Validators.required]);
    }
    valueControl?.updateValueAndValidity({ emitEvent: false });
  }

  private onFieldChange(ruleGroup: FormGroup, field: string | null): void {
    const normalizedField = this.normalizeFieldToken(field);
    const valueControl = ruleGroup.get('value');
    const existingValue = String(valueControl?.value ?? '').trim();

    if (!this.simValueDropFields.has(normalizedField)) {
      this.valueDropdownOptions.set(ruleGroup, []);
      if (!existingValue) {
        valueControl?.setValue('', { emitEvent: false });
      }
      return;
    }

    const selectedEntity = String(this.data?.selectedEntity || '').trim();
    const selectedUuid = String(this.data?.selectedUuid || '').trim();
    this.priceFileSimulationService
      .getValueOptions(String(field || '').trim(), selectedEntity, selectedUuid)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (options) => {
          const resolvedOptions = Array.isArray(options) ? options : [];
          this.valueDropdownOptions.set(ruleGroup, resolvedOptions);
          if (!existingValue) {
            valueControl?.setValue('', { emitEvent: false });
            return;
          }

          const exactMatch = resolvedOptions.find((opt: string) => String(opt).trim() === existingValue);
          if (exactMatch !== undefined) {
            valueControl?.setValue(exactMatch, { emitEvent: false });
            return;
          }

          const caseInsensitiveMatch = resolvedOptions.find((opt: string) =>
            String(opt).trim().toLowerCase() === existingValue.toLowerCase()
          );
          if (caseInsensitiveMatch !== undefined) {
            valueControl?.setValue(caseInsensitiveMatch, { emitEvent: false });
            return;
          }

          // Handle dropdown labels like "37 {Africa}" when saved value is just "Africa" (or "37").
          const normalizedExisting = existingValue.toLowerCase();
          const braceAwareMatch = resolvedOptions.find((opt: string) => {
            const optionText = String(opt || '').trim();
            const match = optionText.match(/^\s*([^{}]+)\{([^}]+)\}\s*$/);
            if (!match) {
              return false;
            }
            const code = String(match[1] || '').trim().toLowerCase();
            const label = String(match[2] || '').trim().toLowerCase();
            const numericExisting = Number(normalizedExisting);
            const numericCode = Number(code);
            const numericCodeMatches = Number.isFinite(numericExisting)
              && Number.isFinite(numericCode)
              && numericExisting === numericCode;
            return normalizedExisting === code || normalizedExisting === label || numericCodeMatches;
          });
          if (braceAwareMatch !== undefined) {
            valueControl?.setValue(braceAwareMatch, { emitEvent: false });
          }
        },
        error: () => {
          this.valueDropdownOptions.set(ruleGroup, []);
          if (!existingValue) {
            valueControl?.setValue('', { emitEvent: false });
          }
        }
      });
  }

  getValueOptionsForRule(control: AbstractControl): string[] {
    return this.valueDropdownOptions.get(control) || [];
  }

  shouldUseValueDropdown(control: AbstractControl): boolean {
    return this.getValueOptionsForRule(control).length > 0;
  }

  setFieldSearchText(control: AbstractControl, value: string): void {
    this.fieldSearchTextByRule.set(control, String(value || '').toLowerCase());
    this.cdr.detectChanges();
  }

  setOperatorSearchText(control: AbstractControl, value: string): void {
    this.operatorSearchTextByRule.set(control, String(value || '').toLowerCase());
    this.cdr.detectChanges();
  }

  setValueSearchText(control: AbstractControl, value: string): void {
    this.valueSearchTextByRule.set(control, String(value || '').toLowerCase());
    this.cdr.detectChanges();
  }

  getFieldSearchText(control: AbstractControl): string {
    return this.fieldSearchTextByRule.get(control) || '';
  }

  getOperatorSearchText(control: AbstractControl): string {
    return this.operatorSearchTextByRule.get(control) || '';
  }

  getValueSearchText(control: AbstractControl): string {
    return this.valueSearchTextByRule.get(control) || '';
  }

  getFilteredFields(control: AbstractControl): Array<{ value: string; viewValue: string }> {
    const query = this.getFieldSearchText(control);
    if (!query) {
      return this.fields;
    }
    return this.fields.filter((field) =>
      String(field?.viewValue || field?.value || '').toLowerCase().includes(query)
    );
  }

  getFilteredOperators(control: AbstractControl): Array<{ value: string; viewValue: string }> {
    const query = this.getOperatorSearchText(control);
    if (!query) {
      return this.operators;
    }
    return this.operators.filter((op) =>
      String(op?.viewValue || op?.value || '').toLowerCase().includes(query)
    );
  }

  getFilteredValueOptions(control: AbstractControl): string[] {
    const options = this.getValueOptionsForRule(control);
    const query = this.getValueSearchText(control);
    if (!query) {
      return options;
    }
    return options.filter((opt: string) => String(opt || '').toLowerCase().includes(query));
  }

  blockSearchOptionSelection(event: any): void {
    if (event?.isUserInput && event?.source) {
      event.source.deselect();
    }
  }

  onSearchDropdownOpened(isOpened: boolean, inputEl?: HTMLInputElement | null): void {
    if (!isOpened) {
      return;
    }
    setTimeout(() => {
      if (!inputEl) {
        return;
      }
      inputEl.focus();
      inputEl.select();
    });
  }

  shouldShowValueControl(control: AbstractControl): boolean {
    const operator = String(control?.get('operator')?.value || '').trim().toLowerCase();
    return operator !== 'is_null'
      && operator !== 'is_not_null'
      && operator !== 'is null'
      && operator !== 'is not null';
  }

  private normalizeFieldToken(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private prefillFromInitialFilters(): void {
    const initialFilters = this.getInitialFiltersFromData();
    if (initialFilters.length > 0) {
      const rootGroup = this.buildRuleSetFromAppliedFilters(initialFilters);
      if (rootGroup) {
        this.queryForm = rootGroup;
        return;
      }

      const rules = this.getRules(this.queryForm);
      rules.clear();
      initialFilters.forEach((filter: any) => rules.push(this.createRuleGroupFromFilter(filter)));
      return;
    }

    this.prefillFromInitialFormState();
  }

  private prefillFromInitialFormState(): boolean {
    const formState = this.data?.initialFormState;
    if (!formState || typeof formState !== 'object') {
      return false;
    }

    const rebuilt = this.buildRuleSetFromFormState(formState);
    if (!rebuilt) {
      return false;
    }

    this.queryForm = rebuilt;
    return true;
  }

  private buildRuleSetFromFormState(state: any): FormGroup | null {
    const rawCondition = String(state?.condition || 'AND').trim().toUpperCase();
    const condition = rawCondition === 'OR' ? 'OR' : 'AND';
    const rawRules = Array.isArray(state?.rules) ? state.rules : [];

    const group = this.createRuleSetGroup();
    group.get('condition')?.setValue(condition, { emitEvent: false });
    const rulesArray = this.getRules(group);
    rulesArray.clear();

    rawRules.forEach((rule: any) => {
      if (rule && Array.isArray(rule.rules)) {
        const nested = this.buildRuleSetFromFormState(rule);
        if (nested) {
          rulesArray.push(nested);
        }
        return;
      }

      const ruleGroup = this.createRuleGroup();
      const fieldValue = this.resolveFieldValueForDialog(String(rule?.field || '').trim());
      const operatorValue = this.resolveOperatorValueForDialog(String(rule?.operator || '=').trim());
      const valueRaw = rule?.value;
      const valueValue = Array.isArray(valueRaw)
        ? valueRaw.map((item: any) => String(item ?? '').trim()).filter((item: string) => item !== '').join(', ')
        : String(valueRaw ?? '').trim();

      ruleGroup.patchValue({
        field: fieldValue,
        operator: operatorValue,
        value: valueValue
      }, { emitEvent: false });

      this.updateValueValidatorForOperator(ruleGroup);
      this.onFieldChange(ruleGroup, fieldValue);
      ruleGroup.get('value')?.updateValueAndValidity({ emitEvent: false });
      rulesArray.push(ruleGroup);
    });

    return group;
  }

  private getInitialFiltersFromData(): any[] {
    if (Array.isArray(this.data?.initialFilters)) {
      return this.data.initialFilters;
    }
    if (Array.isArray(this.data?.appliedFilters)) {
      return this.data.appliedFilters;
    }
    if (Array.isArray(this.data?.requestData?.appliedFilters)) {
      return this.data.requestData.appliedFilters;
    }
    return [];
  }

  private createRuleGroupFromFilter(filter: any): FormGroup {
    const ruleGroup = this.createRuleGroup();
    const rawField = String(filter?.attributeName || filter?.field || '').trim();
    const fieldValue = this.resolveFieldValueForDialog(rawField);
    const operatorValue = this.resolveOperatorValueForDialog(String(filter?.operator || '=').trim());
    const rawValue = Array.isArray(filter?.attributeValues)
      ? filter.attributeValues
      : (Array.isArray(filter?.value) ? filter.value : [filter?.value ?? '']);
    const valueValue = rawValue
      .map((item: any) => String(item ?? '').trim())
      .filter((item: string) => item !== '')
      .join(', ');

    ruleGroup.patchValue({
      field: fieldValue,
      operator: operatorValue,
      value: valueValue
    }, { emitEvent: false });

    this.updateValueValidatorForOperator(ruleGroup);
    this.onFieldChange(ruleGroup, fieldValue);
    ruleGroup.get('value')?.updateValueAndValidity({ emitEvent: false });
    return ruleGroup;
  }

  private buildRuleSetFromAppliedFilters(filters: any[]): FormGroup | null {
    if (!filters.some((f) => f?.beginScope !== undefined || f?.endScope !== undefined || f?.logicalOperator !== undefined)) {
      return null;
    }

    const nodeStack: any[] = [];
    const operatorStack: string[] = [];
    const precedence: Record<string, number> = { OR: 1, AND: 2 };
    const pushOpenScope = () => {
      operatorStack.push('(');
    };
    const closeOneScope = () => {
      while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== '(') {
        applyOperator();
      }
      if (operatorStack[operatorStack.length - 1] === '(') {
        operatorStack.pop();
      }
    };

    const applyOperator = () => {
      const op = operatorStack.pop();
      if (!op || op === '(') {
        return;
      }
      const right = nodeStack.pop();
      const left = nodeStack.pop();
      if (!left || !right) {
        return;
      }
      nodeStack.push({ type: 'group', op, left, right });
    };

    filters.forEach((filter: any, index: number) => {
      const openCount = this.countScopeChars(String(filter?.beginScope || ''), '(');
      for (let i = 0; i < openCount; i++) {
        pushOpenScope();
      }

      nodeStack.push({ type: 'rule', filter });

      const closeCount = this.countScopeChars(String(filter?.endScope || ''), ')');
      for (let i = 0; i < closeCount; i++) {
        closeOneScope();
      }

      const joinToNext = String(filter?.logicalOperator || '').trim().toUpperCase();
      if (index < filters.length - 1 && (joinToNext === 'AND' || joinToNext === 'OR')) {
        while (
          operatorStack.length > 0
          && operatorStack[operatorStack.length - 1] !== '('
          && precedence[operatorStack[operatorStack.length - 1]] >= precedence[joinToNext]
        ) {
          applyOperator();
        }
        operatorStack.push(joinToNext);
      }
    });

    while (operatorStack.length > 0) {
      if (operatorStack[operatorStack.length - 1] === '(') {
        operatorStack.pop();
        continue;
      }
      applyOperator();
    }

    const rootNode = nodeStack.pop();
    if (!rootNode) {
      return null;
    }
    return this.buildFormGroupFromExpressionNode(rootNode);
  }

  private buildFormGroupFromExpressionNode(node: any): FormGroup {
    if (node?.type === 'rule') {
      const root = this.createRuleSetGroup();
      this.getRules(root).push(this.createRuleGroupFromFilter(node.filter));
      return root;
    }

    const op = String(node?.op || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND';
    const group = this.createRuleSetGroup();
    group.get('condition')?.setValue(op, { emitEvent: false });

    const children = this.flattenExpressionByOperator(node, op);
    const rules = this.getRules(group);
    children.forEach((child) => {
      if (child?.type === 'rule') {
        rules.push(this.createRuleGroupFromFilter(child.filter));
      } else {
        rules.push(this.buildFormGroupFromExpressionNode(child));
      }
    });

    return group;
  }

  private flattenExpressionByOperator(node: any, operator: 'AND' | 'OR'): any[] {
    if (!node || node.type !== 'group' || node.op !== operator) {
      return [node];
    }
    return [
      ...this.flattenExpressionByOperator(node.left, operator),
      ...this.flattenExpressionByOperator(node.right, operator)
    ];
  }

  private toAppliedFilters(rootGroup: FormGroup): any[] {
    // Root-level siblings should not be wrapped in scope parentheses.
    // Only nested rule sets should emit beginScope/endScope.
    const segments = this.serializeRuleSetToSegments(rootGroup, true);
    return segments.map((segment, index) => ({
      ...segment,
      logicalOperator: index < segments.length - 1 ? segment.logicalOperator : ''
    }));
  }

  private serializeRuleSetToSegments(group: FormGroup, skipOuterScope: boolean): any[] {
    const condition = String(group.get('condition')?.value || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND';
    const children = this.getRules(group).controls;
    let merged: any[] = [];

    children.forEach((child, childIndex) => {
      const childSegments = this.isRuleSet(child)
        ? this.serializeRuleSetToSegments(child as FormGroup, false)
        : [this.ruleControlToAppliedFilter(child)];
      merged = merged.concat(childSegments);

      if (childIndex < children.length - 1 && merged.length > 0) {
        merged[merged.length - 1].logicalOperator = condition;
      }
    });

    if (!skipOuterScope && merged.length > 1) {
      merged[0].beginScope = `${merged[0].beginScope || ''}(`;
      const lastIndex = merged.length - 1;
      merged[lastIndex].endScope = `${merged[lastIndex].endScope || ''})`;
    }

    return merged;
  }

  private countScopeChars(value: string, ch: '(' | ')'): number {
    const escaped = ch === '(' ? '\\(' : '\\)';
    return (String(value || '').match(new RegExp(escaped, 'g')) || []).length;
  }

  private ruleControlToAppliedFilter(control: AbstractControl): any {
    return {
      field: String(control.get('field')?.value || '').trim(),
      operator: String(control.get('operator')?.value || '').trim(),
      value: String(control.get('value')?.value ?? '').trim(),
      logicalOperator: '',
      beginScope: '',
      endScope: ''
    };
  }

  private resolveFieldValueForDialog(rawField: string): string {
    if (!rawField) {
      return '';
    }
    const normalizedRaw = this.normalizeFieldToken(rawField);
    const exact = this.fields.find((field) => String(field.value || '').trim() === rawField);
    if (exact) {
      return exact.value;
    }
    const normalized = this.fields.find((field) => this.normalizeFieldToken(field.value) === normalizedRaw);
    return normalized ? normalized.value : rawField;
  }

  private resolveOperatorValueForDialog(rawOperator: string): string {
    const operatorMap: Record<string, string> = {
      '=': 'Is equal to',
      '!=': 'Is not equal to',
      '>': 'Greater than',
      '>=': 'Greater than or equal to',
      '<': 'Less than',
      '<=': 'Less than or equal to',
      'in': 'In',
      'not_in': 'Not In',
      'not in': 'Not In',
      'is_null': 'Is Null',
      'is null': 'Is Null',
      'is_not_null': 'Is not Null',
      'is not null': 'Is not Null'
    };

    const normalized = String(rawOperator || '').trim().toLowerCase();
    const preferred = operatorMap[normalized] || rawOperator;
    const exact = this.operators.find((op) => String(op.value || '').trim() === preferred);
    if (exact) {
      return exact.value;
    }

    const fallback = this.operators.find((op) => String(op.value || '').trim().toLowerCase() === normalized);
    if (fallback) {
      return fallback.value;
    }

    // Match operator tokens across variants like "is_null", "is null", "IS NULL"
    const normalizedToken = normalized.replace(/\s+/g, '_');
    const tokenMatch = this.operators.find((op) => {
      const optionToken = String(op.value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
      return optionToken === normalizedToken;
    });
    if (tokenMatch) {
      return tokenMatch.value;
    }

    // Last fallback by view label, e.g. "Is Null"
    const viewMatch = this.operators.find((op) =>
      String(op.viewValue || '').trim().toLowerCase() === String(preferred || '').trim().toLowerCase()
    );
    return viewMatch ? viewMatch.value : preferred;
  }
}
